import { useMercure } from '@composables/useMercure';
import { transchoice } from '@elements/translation';
import { useAuthStore } from '@stores/useAuthStore';
import { Logger } from '@tools/logger/logger';
import { URL_GET_LAST_TIME_USER_ONLINE } from '@tools/routes/routes';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

type OnlineMercureMessage = {
  at: number;
}

export const useOnlineStore = defineStore('online', () => {
  const heartBeatInterval = 15_000;
  const updateOnlineInterval = 1_000;
  const isOnlineDelay = heartBeatInterval * 2;
  const heartBeatTimeout = 5 * 60 * 1_000;

  const { isImpersonated, isLoggedIn, user } = useAuthStore();

  const forceComputedByInterval = ref<number>(0);

  const onlineAtByUser = ref<Map<number, number>>(new Map<number, number>());
  const heartBeatTimer = ref<number | null>(null);

  const getElapsedTime = (timestamp: number): number => (Date.now() / 1000) - timestamp;

  const isOnlineByElapsedTime = (timestamp: number): boolean => getElapsedTime(timestamp) < (isOnlineDelay / 1000);

  const getElapsedTimeMessage = (timestamp: number): string => {
    const minutesUntilNow = Math.max(1, Math.round(getElapsedTime(timestamp) / 60));
    return transchoice('messaging.instant.delay', {
      count: minutesUntilNow,
      minutes: minutesUntilNow,
      hours: Math.floor(minutesUntilNow / 60),
      days: Math.floor(minutesUntilNow / 1440),
      weeks: Math.floor(minutesUntilNow / 10080),
    });
  };

  const getInitialOnlineAt = async (userId: number): Promise<number> => {
    const endpoint = URL_GET_LAST_TIME_USER_ONLINE.replace('{id}', userId.toString());
    const response = await fetch(endpoint);
    return await response.json() as number;
  };

  const subscribeUserOnline = async (userId: number): Promise<void> => {
    // If impersonated or already subscribed to this user, ignore
    if (isImpersonated || onlineAtByUser.value.has(userId)) {
      return;
    }

    const initialOnlineAt = await getInitialOnlineAt(userId);
    onlineAtByUser.value.set(userId, initialOnlineAt);

    const { subscribe } = useMercure(`online/${userId}`);
    await subscribe((message: OnlineMercureMessage) => {
      onlineAtByUser.value.set(userId, message.at);
    });
  };

  const heartBeat = async (): Promise<void> => {
    if (!isLoggedIn || isImpersonated || heartBeatTimer.value) {
      return;
    }

    const { publish } = useMercure(`online/${user!.id}`);

    if (!await publish()) {
      return;
    }

    heartBeatTimer.value = setInterval(async () => {
      if (await publish()) {
        return;
      }

      Logger.error('Unable to publish heartbeat, is user still logged in?');

      if (heartBeatTimer.value) {
        clearInterval(heartBeatTimer.value);
      }
    }, heartBeatInterval);

    setTimeout(() => {
      if (!heartBeatTimer.value) {
        return;
      }

      clearInterval(heartBeatTimer.value);
      heartBeatTimer.value = null;
    }, heartBeatTimeout);
  };

  const isOnlineByUser = computed<Map<number, boolean>>(() => {
    forceComputedByInterval.value;
    const isOnline = new Map<number, boolean>();
    onlineAtByUser.value.forEach((timestamp, userId) => {
      isOnline.set(userId, isOnlineByElapsedTime(timestamp));
    });
    return isOnline;
  });

  const lastOnlineMessageByUser = computed<Map<number, string>>(() => {
    forceComputedByInterval.value;
    const lastOnlineMessage = new Map<number, string>();
    onlineAtByUser.value.forEach((timestamp, userId) => {
      lastOnlineMessage.set(userId, getElapsedTimeMessage(timestamp));
    });
    return lastOnlineMessage;
  });

  // Hack to refresh computed every second (best practice)
  setInterval(() => {
    forceComputedByInterval.value++;
  }, updateOnlineInterval);

  return {
    isOnlineByUser,
    lastOnlineMessageByUser,
    heartBeat,
    subscribeUserOnline,
  };
});
